import {
  Button,
  Center,
  Checkbox,
  Group,
  NavLink,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import {
  IconChevronRight,
  IconDog,
  IconLogout,
  IconMail,
  IconUser,
} from "@tabler/icons-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { client } from "@/api-client";
import type { OwnerProfileInput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { LinkButton } from "@/components/LinkButton";
import { NotificationPrompt } from "@/components/NotificationPrompt";
import { Portrait } from "@/components/Portrait";
import { useOwnerProfileDisplay } from "@/hooks/useProfileDisplay";
import { OwnerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import { useSignOut } from "@/utils/signout";

export const ownerProfileNavLinks = [
  {
    label: "Personal Information",
    icon: IconUser,
    route: UniversalRoutes.PROFILE.PERSONAL_INFO,
  },
  {
    label: "Dog Information",
    icon: IconDog,
    route: OwnerRoutes.PROFILE.DOGS,
  },
  {
    label: "Notifications",
    icon: IconMail,
    route: UniversalRoutes.PROFILE.NOTIFICATIONS,
  },
  {
    label: "Sign out",
    icon: IconLogout,
    signOut: true as const,
  },
];

export const AccountSettings = () => {
  const { navigator } = useNavigator();
  const { profile } = useOwnerProfileDisplay();
  const signOut = useSignOut();

  if (!profile) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Title order={1}>Profile Settings</Title>
      <NotificationPrompt />
      <SimpleGrid cols={2}>
        <Stack>
          <Center>
            <Portrait profile={profile} size="8em" />
          </Center>
          <Center>
            <LinkButton
              href={UniversalRoutes.PROFILE.PORTRAIT}
              variant="outline"
              color="black"
            >
              Change
            </LinkButton>
          </Center>
        </Stack>
        <Stack>
          <Center>
            <Portrait profile={profile.dogs[0]} size="8em" who="dog" />
          </Center>
          <Center>
            <LinkButton
              href={OwnerRoutes.PROFILE.DOGS_PORTRAIT}
              variant="outline"
              color="black"
            >
              Change
            </LinkButton>
          </Center>
        </Stack>
      </SimpleGrid>
      {ownerProfileNavLinks.map((link, index) => (
        <NavLink
          key={index}
          pl={0}
          p={0}
          mt="xs"
          label={
            <Group justify="space-between" wrap="nowrap" w="100%">
              <Text size="1.5em">{link.label}</Text>
            </Group>
          }
          leftSection={<link.icon size="1.5em" />}
          rightSection={
            <IconChevronRight
              size="1.5em"
              stroke={1.5}
              className="mantine-rotate-rtl"
            />
          }
          onClick={link.signOut ? signOut : navigator({ path: link.route })}
        />
      ))}
    </Stack>
  );
};

const OwnerProfileWhenLoaded = ({
  profileData,
}: {
  profileData: OwnerProfileInput;
}) => {
  const { navigate } = useNavigator();

  const backendValuesToFormValues = (
    backendProfileData?: OwnerProfileInput,
  ) => {
    if (!backendProfileData) {
      return undefined;
    }
    return {
      firstName: backendProfileData.firstName,
      lastName: backendProfileData.lastName,
      phone: backendProfileData.phone,
      locality: backendProfileData.locality,
    };
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: backendValuesToFormValues(profileData) ?? {
      firstName: "",
      lastName: "",
      phone: "",
      locality: "",
    },

    validate: {
      firstName: isNotEmpty("First name is required"),
      lastName: isNotEmpty("Last name is required"),
      phone: isNotEmpty("Phone number is required"),
      locality: isNotEmpty("City is required"),
    },
  });

  const updateOwnerProfile = client.useMutation("post", "/owner/profile/");

  const handleSubmit = async (values: typeof form.values) => {
    await updateOwnerProfile.mutateAsync({
      body: {
        ...values,
        dogs: profileData.dogs,
      },
    });
    if (!profileData) {
      await fetchAuthSession({ forceRefresh: true });
    }
    setTimeout(() => {
      navigate({ path: UniversalRoutes.PROFILE.HOME });
    }, 1);
  };

  return (
    <Stack>
      <Header title="Personal Information" />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          withAsterisk
          label="First name"
          key={form.key("firstName")}
          {...form.getInputProps("firstName")}
        />
        <TextInput
          withAsterisk
          label="Last name"
          key={form.key("lastName")}
          {...form.getInputProps("lastName")}
        />
        <TextInput
          withAsterisk
          label="Phone"
          key={form.key("phone")}
          {...form.getInputProps("phone")}
        />
        <TextInput
          withAsterisk
          label="City"
          key={form.key("locality")}
          {...form.getInputProps("locality")}
        />

        {!profileData && (
          <Checkbox
            mt="md"
            label="I agree to conditions"
            key={form.key("acceptedToc")}
            {...form.getInputProps("acceptedToc", {
              type: "checkbox",
            })}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            fullWidth
            loading={updateOwnerProfile.isPending}
          >
            Save
          </Button>
        </Group>
      </form>
    </Stack>
  );
};

export const OwnerProfile = () => {
  const user = useUser();
  const profile = client.useQuery(
    "get",
    "/owner/profile",
    {
      enabled: user.isProfileCreated,
    },
    {
      gcTime: 0,
      staleTime: 0,
    },
  );

  if (profile.data) {
    return <OwnerProfileWhenLoaded profileData={profile.data} />;
  }

  return <FancyLoader />;
};
